import React, { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { useLoader } from "../../contexts/LoaderContext";
import { STATUS_CODES } from "../../utils/statusCode";
import Toster from "../../utils/Toaster";
import {
  handleAdminFetchProfile,
  handleAdminUpdateProfile,
} from "../../services/APIClient";
import {
  updateUserProfile,
  removeUserData,
} from "../../store/slices/userSlice";

const EditProfileModal = ({ isOpen, onClose }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { setLoading } = useLoader();
  const { userData } = useSelector((state) => state.user);

  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm({ mode: "onChange" });

  const [imagePreview, setImagePreview] = useState(null);

  const fetchProfile = async () => {
    try {
      setLoading(true);
      const response = await handleAdminFetchProfile();

      if (!response) {
        Toster("Failed to fetch profile data", "error");
        return;
      }

      if (response.code === STATUS_CODES.SUCCESS) {
        const profileData = response.data;
        setValue("fullname", profileData?.fullname || "");

        if (profileData?.profileImg) {
          setImagePreview(profileData.profileImg);
        }
      } else if (response.code === STATUS_CODES.INVALID_TOKEN) {
        dispatch(removeUserData({}));
        navigate("/login");
      } else {
        Toster("Failed to fetch profile data", "error");
      }
    } catch (error) {
      Toster("Error fetching profile", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen) {
      fetchProfile();
    }
  }, [isOpen]);

  const onSubmit = async (formdata) => {
    const postData = new FormData();
    postData.append("fullname", formdata.fullname);

    if (formdata.profileImage?.[0]) {
      postData.append("profileImg", formdata.profileImage[0]);
    }

    try {
      setLoading(true);
      const response = await handleAdminUpdateProfile(postData);

      if (response?.code === STATUS_CODES.SUCCESS) {
        const updatedData = response.data;

        if (updatedData.profileImg) {
          dispatch(updateUserProfile({ value: updatedData.profileImg }));
          setImagePreview(updatedData.profileImg);
        }

        Toster("Profile updated successfully", "success");
        onClose();
      } else if (response?.code === STATUS_CODES.INVALID_TOKEN) {
        dispatch(removeUserData({}));
        navigate("/login");
      } else {
        Toster("Failed to update profile", "error");
      }
    } catch (error) {
      Toster("Error updating profile", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleImageChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const validTypes = ["image/jpeg", "image/jpg", "image/png"];
    if (!validTypes.includes(file.type)) {
      Toster("Only JPG, JPEG, and PNG files are allowed", "error");
      e.target.value = "";
      return;
    }

    const fileSizeMB = file.size / (1024 * 1024);
    if (fileSizeMB > 5) {
      Toster("File size should be less than 5MB", "error");
      return;
    }

    setImagePreview(URL.createObjectURL(file));
  };

  const handleClose = () => {
    setImagePreview(null);
    setValue("fullname", "");
    setValue("profileImage", "");
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="bg-white w-[90%] sm:w-full max-w-[550px] rounded-lg p-6 shadow-lg overflow-y-auto max-h-[90vh]">
        <h2 className="text-center text-xl font-semibold text-gray-800 mb-4">
          Edit Profile
        </h2>

        <form onSubmit={handleSubmit(onSubmit)}>
          {/* Fullname Field */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Fullname
            </label>
            <input
              type="text"
              maxLength={20}
              className={`w-full text-black px-4 py-2 border ${
                errors.fullname ? "border-red-500" : "border-gray-300"
              } rounded-md focus:outline-none focus:ring-2 ${
                errors.fullname ? "focus:ring-red-500" : "focus:ring-blue-500"
              }`}
              placeholder="Enter your fullname"
              {...register("fullname", {
                required: "Fullname is required",
                minLength: {
                  value: 3,
                  message: "Fullname must be at least 3 characters",
                },
                maxLength: {
                  value: 20,
                  message: "Fullname cannot exceed 20 characters",
                },
              })}
              onInput={(e) => {
                e.target.value = e.target.value.replace(/^\s+/, "");
              }}
            />
            {errors.fullname && (
              <p className="text-red-500 text-sm mt-1">
                {errors.fullname.message}
              </p>
            )}
          </div>

          {/* Profile Image Field */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Profile Image
            </label>
            <input
              type="file"
              accept="image/jpeg,image/jpg,image/png"
              className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:border file:border-gray-300 file:rounded file:text-sm file:bg-gray-50 file:text-gray-700 hover:file:bg-gray-100 cursor-pointer"
              {...register("profileImage")}
              onChange={handleImageChange}
            />
            {(imagePreview || userData.profileImg) && (
              <div className="flex justify-center mt-4">
                <img
                  src={imagePreview || userData.profileImg}
                  alt="Profile"
                  className="max-h-40 w-auto rounded"
                />
              </div>
            )}
          </div>

          {/* Action Buttons */}
          <div className="flex  sm:flex-row justify-between gap-12 mt-4">
            <button
              type="button"
              onClick={handleClose}
              className="w-full sm:w-auto px-4 py-2 bg-gray-300 text-gray-700 rounded hover:bg-gray-400"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="w-full sm:w-auto px-4 py-2 bg-gray-700 text-white rounded hover:bg-gray-900 focus:outline-none focus:ring-2 focus:ring-gray-700 focus:ring-offset-2"
            >
              Update
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditProfileModal;
